import { gql } from '@apollo/client';
import {
  ATLAS_AGENT_STALE_MIN,
  ATLAS_CAP,
  ATLAS_CRIT,
  ATLAS_WARN,
  atlasLevel,
  type AtlasEntryState,
  type AtlasLevel,
} from '@infras/shared-types';

/**
 * Shared by the three Atlas screens — index, project, mappings.
 *
 * Thresholds are NOT redefined here: they come from shared-types, the same module
 * the resolver imports. A capacity bar that turns amber at a different number than
 * the API's alert is not a styling bug, it is two different claims about whether
 * production is about to lose database access.
 */

export { ATLAS_CAP, ATLAS_WARN, ATLAS_CRIT, ATLAS_AGENT_STALE_MIN, atlasLevel };
export type { AtlasEntryState, AtlasLevel };

// ── the wire ─────────────────────────────────────────────────────────────────

export interface AtlasProjectOverview {
  projectKey: string;
  projectId: string;
  displayName?: string | null;
  isActive: boolean;
  hasCredentials: boolean;
  liveCount?: number | null;
  pendingCount?: number | null;
  liveCountAt?: string | null;
  reportedTotal: number;
  clusterCount: number;
  agentsTotal: number;
  agentsStale: number;
  lastSyncAt?: string | null;
  lastSyncStatus?: string | null;
}

export interface AtlasEntryRow {
  cidr: string;
  comment?: string | null;
  state: AtlasEntryState;
  clusterName?: string | null;
  nodeName?: string | null;
  lastSeenAt?: string | null;
}

export interface AtlasHeartbeat {
  clusterName: string;
  agentCount: number;
  lastSeenAt: string;
}

export interface UnmappedCluster {
  clusterName: string;
  agentCount: number;
  ignored: boolean;
  ignoredReason?: string | null;
  ignoredBy?: string | null;
}

export const PROJECTS_OVERVIEW = gql`
  query AtlasProjectsOverview {
    atlasProjectsOverview {
      projectKey
      projectId
      displayName
      isActive
      hasCredentials
      liveCount
      pendingCount
      liveCountAt
      reportedTotal
      clusterCount
      agentsTotal
      agentsStale
      lastSyncAt
      lastSyncStatus
    }
  }
`;

export const ATLAS_ENTRIES = gql`
  query AtlasEntries($projectKey: String!) {
    atlasEntries(projectKey: $projectKey) {
      cidr
      comment
      state
      clusterName
      nodeName
      lastSeenAt
    }
  }
`;

export const ATLAS_PENDING = gql`
  query AtlasPendingIps($projectKey: String!) {
    atlasPendingIps(projectKey: $projectKey) {
      id
      ipCidr
      clusterName
      nodeName
      lastSeenAt
    }
  }
`;

export const ATLAS_HEARTBEATS = gql`
  query AtlasAgentHeartbeats($projectKey: String) {
    atlasAgentHeartbeats(projectKey: $projectKey) {
      clusterName
      agentCount
      lastSeenAt
    }
  }
`;

export const ATLAS_UNMAPPED = gql`
  query AtlasUnmappedClusters {
    atlasUnmappedClusters {
      clusterName
      agentCount
      ignored
      ignoredReason
      ignoredBy
    }
  }
`;

export const SET_IGNORED = gql`
  mutation SetClusterAtlasIgnored($clusterName: String!, $ignored: Boolean!, $reason: String) {
    setClusterAtlasIgnored(clusterName: $clusterName, ignored: $ignored, reason: $reason)
  }
`;

export const REFRESH_LIVE_COUNT = gql`
  mutation RefreshAtlasLiveCount($projectKey: String!) {
    refreshAtlasLiveCount(projectKey: $projectKey) {
      projectKey
      liveCount
      pendingCount
      liveCountAt
    }
  }
`;

// ── presentation ─────────────────────────────────────────────────────────────

/** Level → the CSS var that colours the number, bar and headline together. */
export function levelColor(level: AtlasLevel | null): string {
  if (level === 'crit') return 'var(--bad)';
  if (level === 'warn') return 'var(--amber)';
  if (level === 'ok') return 'var(--green)';
  // Unknown usage. Muted, never green: "we have not looked" must not read as "fine".
  return 'var(--faint)';
}

export const STATE_LABEL: Record<AtlasEntryState, string> = {
  in_sync: 'in sync',
  no_agent: 'no agent',
  stale: 'stale',
  manual: 'manual',
  open_to_all: 'open to all',
};

/** What each state means, on hover. The words alone don't carry the consequence. */
export const STATE_TITLE: Record<AtlasEntryState, string> = {
  in_sync: 'In Atlas, and an agent reports it.',
  no_agent: 'In Atlas, but nothing reports it — it occupies one of the 200 slots for nothing.',
  stale: 'An agent reported this, but not recently. The node may be gone.',
  manual: 'Added by hand in Atlas, outside this system. Never counted as reclaimable.',
  open_to_all: 'This CIDR admits every address on the internet, which makes the rest of the list decorative.',
};

/**
 * Capacity arithmetic, in one place.
 *
 * `projected` is what the NEXT reconcile would leave behind, and `rejected` is the
 * case that must shout: past the cap Atlas rejects the add outright rather than
 * truncating, so nodes silently lose database access.
 */
export function capacity(live: number | null | undefined, pending: number | null | undefined) {
  const known = live != null;
  const l = live ?? 0;
  const p = pending ?? 0;
  const projected = l + p;
  return {
    known,
    live: l,
    pending: p,
    projected,
    free: Math.max(0, ATLAS_CAP - projected),
    slotsLeft: Math.max(0, ATLAS_CAP - l),
    rejected: projected > ATLAS_CAP,
    level: atlasLevel(live ?? null),
    /** Percentage widths for the two-segment bar. Always over ATLAS_CAP — never
     *  normalised to the biggest project, or the cap stops meaning anything. */
    livePct: known ? Math.min(100, (l / ATLAS_CAP) * 100) : 0,
    pendingPct: known ? Math.min(100 - (l / ATLAS_CAP) * 100, (p / ATLAS_CAP) * 100) : 0,
  };
}

/** Minutes since a timestamp, for the heartbeat colouring. */
export function minutesSince(iso?: string | null): number | null {
  if (!iso) return null;
  return (Date.now() - new Date(iso).getTime()) / 60000;
}

/** Heartbeat severity: green under 15 min, amber to 30, red past it. */
export function heartbeatColor(mins: number | null): string {
  if (mins == null) return 'var(--faint)';
  if (mins > ATLAS_AGENT_STALE_MIN) return 'var(--bad)';
  if (mins > 15) return 'var(--amber)';
  return 'var(--green)';
}
